import moment from "moment-timezone";
import { ApiActions } from "middleware/call-api";
import prepareSearchReferralsParamsForBody from "helpers/format-search-referrals-params-for-request-body";
import { getReferralWorklistSettings } from "app/auth/store/reducers/system-configuration";
import { formattedReferralWorkListFilterTags } from "app/main/referralWorklist/actions/referralWorklist.actions";
import { isPageLoading, isPageLoaded, REFERRALS_PAGE_SIZE, isPatientActiveReferralsLoading, isPatientPresentingComplaintsLoading, ActionTypes } from "../reducers/patientReferrals.reducers";

const inFlightPatientReferralRequests = new Set();

// Create stable JSON string by sorting object keys recursively for consistent cache keys
const stableStringify = obj => JSON.stringify(obj, (key, value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value)
      .sort()
      .reduce((sorted, k) => ({
        ...sorted,
        [k]: value[k],
      }), {});
  }
  return value;
});

const timeZone = moment.tz.guess();

const getDefaultSpecialtySearchParams = state => {
  const referralWorklistSettings = getReferralWorklistSettings(state);
  const defaultSorOrder = referralWorklistSettings?.worklistSortOrder;

  return {
    showOldestFirst: defaultSorOrder === "asc",
  };
};

export const resetPatientActiveReferralSearch = () => ({
  type: ActionTypes.RESET_PATIENT_REFERRALS_SEARCH,
});

export const setUpPatientReferralDefaultSearchParams = () => (dispatch, getState) => {
  const defaultSpecialtySearchParams = getDefaultSpecialtySearchParams(getState());

  return dispatch({
    type: ActionTypes.SET_DEFAULT_PATIENT_REFERRALS_SEARCH_PARAMS,
    payload: defaultSpecialtySearchParams,
  });
};

export const fetchPatientReferrals = (searchParams, pageNumber = 1, forceReload = false) => dispatch => {
  const patientId = searchParams?.patientId;
  const orgUnitId = searchParams?.orgUnitId;

  const processedParams = prepareSearchReferralsParamsForBody(searchParams);
  const url = `/referrals/${orgUnitId}/SearchPatientReferrals?pageNumber=${pageNumber}&pageSize=${REFERRALS_PAGE_SIZE}&timeZone=${timeZone}`;
  const requestKey = `${url}|${stableStringify(processedParams)}`;

  if (inFlightPatientReferralRequests.has(requestKey)) {
    return Promise.resolve(null);
  }

  inFlightPatientReferralRequests.add(requestKey);

  return dispatch({
    [ApiActions.FETCH_FROM_API]: {
      endpoint: url,
      method: "POST",
      body: { searchParams: processedParams },
      entityType: "PATIENT_REFERRALS",
      patientId,
      pageNumber,
      orgUnitId,
      bailout: state => isPageLoading(state, orgUnitId, patientId, pageNumber) || (!forceReload && isPageLoaded(state, orgUnitId, patientId, pageNumber)),
    },
  }).finally(() => {
    inFlightPatientReferralRequests.delete(requestKey);
  });
};

export const fetchPatientActiveReferrals = (orgUnitId, patientId, takeNumber) => dispatch => {
  const url = `/referrals/${orgUnitId}/SearchPatientReferrals?pageNumber=1&pageSize=${takeNumber ?? 2}&timeZone=${timeZone}`;

  const searchParams = {
    activeOnly: true,
    patientId,
  };
  const processedParams = prepareSearchReferralsParamsForBody(searchParams);

  return dispatch({
    [ApiActions.FETCH_FROM_API]: {
      endpoint: url,
      method: "POST",
      body: { searchParams: processedParams },
      entityType: "PATIENT_ACTIVE_REFERRALS",
      patientId,
      orgUnitId,
      bailout: state => isPatientActiveReferralsLoading(state, patientId),
    },
  });
};

export const fetchPatientPresentingComplaints = (orgUnitId, patientId) => dispatch => {
  const url = `/referrals/${orgUnitId}/getPatientPresentingComplaints/${patientId}`;

  return dispatch({
    [ApiActions.FETCH_FROM_API]: {
      endpoint: url,
      entityType: "PATIENT_SPECIALTY_PRESENTING_COMPLAINTS",
      patientId,
      bailout: state => isPatientPresentingComplaintsLoading(state),
    },
  });
};

export const updatePatientReferralWorkFilterTags = (searchParams, referralFieldLabels) => (dispatch, getState) => {
  const tags = formattedReferralWorkListFilterTags(searchParams, referralFieldLabels, getState);

  return dispatch({
    type: ActionTypes.UPDATE_UPDATE_PATIENT_REFERRAL_FILTER_TAGS,
    payload: tags,
  });
};

export const resetPatientActiveReferralsLoaded = patientId => ({
  type: ActionTypes.RESET_PATIENT_ACTIVE_REFERRALS_LOADED,
  payload: { patientId },
});
